import { ApolloError, useMutation } from '@apollo/client';
import { Alert, FullBleed } from '@phx/design-system';
import { useFullNameFieldValidation } from '@phx/design-system/hooks';
import { KnownErrorCode } from '@phx/instrumentation/client';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import {
    type VerifyFormDetails,
    VerifyFormProvider,
    useVerifyForm,
} from '../../../components/cabinet/prescription/verify/verify-form';
import { useVerifyFormSchema } from '../../../components/cabinet/prescription/verify/verify-form/use-verify-form-schema';
import { VerifyPrescription } from '../../../components/cabinet/prescription/verify/VerifyPrescription';
import { getFragment } from '../../../graphql';
import {
    ClaimPrescriberOrderForNewPatientDocument,
    GetPrescriberOrdersByPatientDocument,
    GetPrescriberOrdersDocument,
    GetUnclaimedPrescriberOrderDocument,
    PrescriberOrderStatus,
} from '../../../graphql/generated/graphql';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { QueryLoader } from '../../../loaders';
import { getAbsoluteRoute } from '../../../util';
import { getErrorCode } from '../../../util/get-error-code';
import routes from '../../app-routes';

export const VerifyPrescriptionRoute = () => {
    const { prescriptionId } = useParams();
    if (prescriptionId === undefined) {
        // TODO: Better validation
        throw Error('Prescription ID is required');
    }
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { primaryPatientId, scopedPatientId } = usePatientContext();

    const refetchQueries = [
        {
            query: GetPrescriberOrdersDocument,
            variables: { patientId: primaryPatientId },
        },
        {
            query: GetPrescriberOrdersByPatientDocument,
            variables: {
                patientId: scopedPatientId,
                input: { statusFilter: [PrescriberOrderStatus.New] },
            },
        },
    ];

    const [submissionError, setSubmissionError] = useState<string | null>(null);
    const form = useVerifyForm({
        initialValues: {
            firstName: '',
            lastName: '',
            dob: '',
            attest: false,
        },
        schema: useVerifyFormSchema(),
        validateInputOnBlur: true,
        onValuesChange: () => {
            setSubmissionError(null);
        },
    });

    useFullNameFieldValidation(form);

    const [ClaimPrescriberOrderForNewPatient] = useMutation(
        ClaimPrescriberOrderForNewPatientDocument,
        {
            refetchQueries,
            awaitRefetchQueries: true,
        }
    );

    const handleMerge = useCallback(async (formData: VerifyFormDetails) => {
        const { firstName, lastName, dob } = formData;
        const [month, day, year] = dob.split('/');

        try {
            await ClaimPrescriberOrderForNewPatient({
                variables: {
                    prescriberOrderId: prescriptionId,
                    patientDetails: {
                        firstName,
                        lastName,
                        dateOfBirth: `${year}-${month}-${day}`,
                    },
                },
            });
        } catch (error: unknown) {
            if (error instanceof ApolloError) {
                if (getErrorCode(error) === KnownErrorCode.VerifyDoesNotMatch) {
                    setSubmissionError(t('verifyPrescription.mismatchError'));
                }
                return;
            }

            setSubmissionError(t('verifyPrescription.error'));
            return;
        }

        navigate(getAbsoluteRoute('cabinet.rxId.root', { prescriptionId }));
    }, []);

    const Loader = QueryLoader<typeof GetUnclaimedPrescriberOrderDocument>;
    return (
        <Loader
            query={GetUnclaimedPrescriberOrderDocument}
            variables={{ prescriberOrderId: prescriptionId }}
            render={(data) => {
                if (data.unclaimedPrescriberOrder) {
                    const prescriberOrder = getFragment(
                        data.unclaimedPrescriberOrder
                    );
                    return (
                        <>
                            {submissionError && (
                                <FullBleed
                                    breakpoint="md"
                                    color="error"
                                    spacing="none"
                                >
                                    <Alert severity="error">
                                        {submissionError}
                                    </Alert>
                                </FullBleed>
                            )}
                            <VerifyFormProvider form={form}>
                                <VerifyPrescription
                                    prescriberOrder={prescriberOrder}
                                    claimPrescription={handleMerge}
                                />
                            </VerifyFormProvider>
                        </>
                    );
                }
                return <Navigate to={routes.home} />;
            }}
        />
    );
};
